/*
 * TFRS Navbar — Single-row: logo left, 5-item nav center, phone + CTA right
 *
 * Nav structure:
 *  1. Emergency Response  → Build Your Vehicle (gold), Law Enforcement, Fire/Brush Trucks,
 *                           K9 Consultation, Federal Contracting
 *  2. Commercial          → /commercial, Municipal Fleet Vehicle Protection
 *  3. Supply Store        → Texas First Response Supply (account-claim page),
 *                           Federal Signal Wholesale Pricing (launch page)
 *  4. Company             → Capabilities, About Us, Gallery, Resources, Contact Us
 *  5. BUILD YOUR VEHICLE  → /configure (gold, standalone CTA)
 *
 * Accessibility (May 2026 audit):
 *  - No <button><Link> or <Link><button> nesting
 *  - aria-expanded + aria-haspopup on all dropdown triggers
 *  - role="menu" / role="menuitem" on dropdown panels/items
 *  - Keyboard: Enter/Space toggles, Escape closes, ArrowDown opens & focuses first item
 *  - aria-expanded on hamburger
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, Phone, MessageSquareText, ChevronDown, ExternalLink } from "lucide-react";
import { PHONE_DISPLAY, SMS_PHONE_DISPLAY, SMS_URI } from "@shared/companyFacts";

// ─── Nav data ──────────────────────────────────────────────────────────────────

interface SubItem {
  label: string;
  href: string;
  gold?: boolean;
  external?: boolean;
  dividerAfter?: boolean;
  comingSoon?: boolean;
}

interface NavItem {
  label: string;
  href?: string;
  gold?: boolean;
  dropdown?: SubItem[];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: "Emergency Response",
    href: "/law-enforcement",
    dropdown: [
      { label: "Build Your Vehicle", href: "/configure", gold: true, dividerAfter: true },
      { label: "Law Enforcement", href: "/law-enforcement" },
      { label: "Fire / Brush Trucks", href: "/fire-brush-trucks" },
      { label: "Crew Cab Brush Truck (For Sale)", href: "/crew-cab-brush-truck" },
      { label: "K9 Consultation", href: "/k9-consult" },
      { label: "Federal Contracting", href: "/federal-contracting" },
    ],
  },
  {
    label: "Commercial",
    href: "/commercial",
    dropdown: [
      { label: "Commercial & Work Truck Upfitting", href: "/commercial" },
      { label: "Municipal Fleet Vehicle Protection", href: "/municipal-fleet-vehicle-protection" },
    ],
  },
  {
    label: "Supply Store",
    dropdown: [
      { label: "Texas First Response Supply", href: "/supply-store" },
      { label: "Federal Signal Wholesale Pricing", href: "/federal-signal-launch" },
    ],
  },
  {
    label: "Company",
    dropdown: [
      { label: "Capabilities", href: "/capabilities", dividerAfter: true },
      { label: "About Us", href: "/about" },
      { label: "Gallery", href: "/gallery" },
      { label: "Resources", href: "/resources" },
      { label: "Contact Us", href: "/contact" },
    ],
  },
  {
    label: "Build Your Vehicle",
    href: "/configure",
    gold: true,
  },
];

// ─── Dropdown component ────────────────────────────────────────────────────────

function DropdownMenu({
  item,
  location,
}: {
  item: NavItem;
  location: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus first item on keyboard open
  useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((p) => !p);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  const isActive =
    item.href === location ||
    (item.dropdown?.some((s) => s.href === location) ?? false);

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Trigger row: label Link + chevron button */}
      <div
        className={`flex items-center border-b-2 transition-colors ${
          isActive ? "border-[var(--tfrs-red-bright)]" : "border-transparent hover:border-white/20"
        }`}
      >
        {item.href ? (
          <Link
            href={item.href}
            className={`px-3 py-4 font-eyebrow text-xs font-medium tracking-wider whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)] ${
              isActive ? "text-[var(--tfrs-red-bright)]" : "text-white hover:text-white/90"
            }`}
          >
            {item.label}
          </Link>
        ) : (
          <span
            className={`px-3 py-4 font-eyebrow text-xs font-medium tracking-wider whitespace-nowrap cursor-default ${
              isActive ? "text-[var(--tfrs-red-bright)]" : "text-white"
            }`}
          >
            {item.label}
          </span>
        )}
        <button
          onClick={() => setOpen((p) => !p)}
          onKeyDown={handleKeyDown}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={`Expand ${item.label} submenu`}
          className={`px-3 py-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)] ${
            isActive ? "text-[var(--tfrs-red-bright)]" : "text-white hover:text-white/90"
          }`}
        >
          <ChevronDown
            size={11}
            className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div
          role="menu"
          aria-label={`${item.label} submenu`}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-0 min-w-[220px] bg-[var(--tfrs-navy-panel)] border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.7)] z-50"
        >
          {/* Arrow tip */}
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[var(--tfrs-navy-panel)] border-l border-t border-white/10 rotate-45" />
          <div className="py-1">
            {item.dropdown!.map((sub, idx) => (
              <div key={sub.label}>
                {sub.dividerAfter && idx > 0 && (
                  <div className="border-t border-white/10 my-1" />
                )}
                <Link
                  href={sub.href}
                  role="menuitem"
                  ref={idx === 0 ? firstItemRef : undefined}
                  className={`flex items-center gap-2 px-5 py-3 font-eyebrow text-xs font-medium tracking-wider transition-colors border-l-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy-panel)] ${
                    sub.gold
                      ? location === sub.href
                        ? "text-[#c9a84c] border-[#c9a84c] bg-[#c9a84c]/5"
                        : "text-[#c9a84c] border-transparent hover:border-[#c9a84c] hover:bg-[#c9a84c]/5"
                      : location === sub.href
                      ? "text-[var(--tfrs-red-bright)] border-[var(--tfrs-red-bright)] bg-white/5"
                      : "text-white hover:text-white/90 border-transparent hover:border-[var(--tfrs-red-bright)] hover:bg-white/5"
                  }`}
                >
                  {sub.label}
                  {sub.external && <ExternalLink size={10} className="opacity-50" />}
                  {sub.comingSoon && (
                    <span className="ml-auto text-xs font-bold uppercase tracking-widest text-[#c9a84c] bg-[#c9a84c]/10 border border-[#c9a84c]/30 px-1.5 py-0.5 rounded">
                      Soon
                    </span>
                  )}
                </Link>
                {sub.dividerAfter && (
                  <div className="border-t border-white/10 my-1" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Navbar ───────────────────────────────────────────────────────────────

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  const [location] = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setExpandedMobile(null);
  }, [location]);

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[var(--tfrs-navy)]/98 shadow-[0_4px_20px_rgba(0,0,0,0.6)] backdrop-blur-sm"
          : "bg-[var(--tfrs-navy)]"
      }`}
    >
      {/* ── Single header row ── */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 flex items-center h-16 gap-4">

          {/* Logo — left */}
          <Link
            href="/"
            className="flex-shrink-0 flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
            aria-label="Texas First Response Services — Home"
          >
            {/* Text-based logo — white and gold */}
            <span className="font-eyebrow font-black text-sm tracking-widest uppercase text-white leading-tight">
              TEXAS FIRST<br />
              <span className="text-[#c9a84c]">RESPONSE SERVICES</span>
            </span>
          </Link>

          {/* Desktop nav — center (hidden on mobile) */}
          <div className="hidden xl:flex flex-1 items-center justify-center gap-0">
            {NAV_ITEMS.map((item) => {
              if (item.dropdown) {
                return (
                  <DropdownMenu key={item.label} item={item} location={location} />
                );
              }
              // Standalone link (Build Your Vehicle gold CTA)
              return (
                <Link
                  key={item.label}
                  href={item.href!}
                  className={`block px-4 py-4 font-eyebrow text-xs font-medium tracking-wider whitespace-nowrap transition-colors border-b-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)] ${
                    item.gold
                      ? location === item.href
                        ? "text-[#c9a84c] border-[#c9a84c]"
                        : "text-[#c9a84c] border-[#c9a84c]/40 hover:border-[#c9a84c]"
                      : location === item.href
                      ? "text-[var(--tfrs-red-bright)] border-[var(--tfrs-red-bright)]"
                      : "text-white hover:text-white/90 border-transparent hover:border-white/20"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* Right side: call/text + CTA */}
          <div className="flex-shrink-0 flex items-center gap-3 ml-auto lg:ml-0">
            <div className="hidden 2xl:flex items-center gap-2">
              <a
                href="tel:+18322252838"
                className="btn-chip focus-visible:outline-none"
              >
                <Phone size={13} />
                Call
              </a>
              <a
                href={SMS_URI}
                className="btn-chip focus-visible:outline-none"
              >
                <MessageSquareText size={13} />
                Text Us
              </a>
            </div>
            {/*
             * The full desktop nav only fits starting at xl (1280px) --
             * forcing it to show earlier overflows the header between
             * ~1024-1180px (measured live). But "Request a Quote" is a
             * small, self-contained button that fits fine well below that,
             * and it's the one element the 768-1280px gap audit finding
             * was actually about: without this, the primary CTA vanished
             * completely in that range, reachable only by opening the
             * hamburger menu (which repeats this same link, see below).
             * Showing it here from md (768px) up closes that gap without
             * touching the nav/hamburger breakpoint that's actually
             * constrained by available width.
             */}
            <Link
              href="/quote"
              className="btn-cta hidden md:block text-xs px-4 py-2.5 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
            >
              <span>Request a Quote</span>
            </Link>
            {/* Hamburger */}
            <button
              className="xl:hidden text-white p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile menu ── */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="xl:hidden bg-[var(--tfrs-navy)] border-t border-white/10 max-h-[80vh] overflow-y-auto"
        >
          {NAV_ITEMS.map((item) => {
            if (item.dropdown) {
              const isExpanded = expandedMobile === item.label;
              const isActive =
                item.href === location ||
                item.dropdown.some((s) => s.href === location);
              return (
                <div key={item.label}>
                  <div className="flex items-center border-b border-white/5">
                    {item.href ? (
                      <Link
                        href={item.href}
                        className={`flex-1 px-5 py-4 font-eyebrow text-sm tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)] ${
                          isActive ? "text-[var(--tfrs-red-bright)]" : "text-white hover:bg-white/5"
                        }`}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <span
                        className={`flex-1 px-5 py-4 font-eyebrow text-sm tracking-wider ${
                          isActive ? "text-[var(--tfrs-red-bright)]" : "text-white"
                        }`}
                      >
                        {item.label}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setExpandedMobile(isExpanded ? null : item.label)}
                      className="px-4 py-4 text-white hover:text-white/90 transition-colors border-l border-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
                      aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.label} submenu`}
                      aria-expanded={isExpanded}
                    >
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>
                  {isExpanded &&
                    item.dropdown.map((sub) => (
                      <Link
                        key={sub.label}
                        href={sub.href}
                        aria-label={sub.comingSoon ? `${sub.label} — coming soon` : undefined}
                        className={`flex items-center gap-2 pl-10 pr-5 py-3 font-eyebrow text-xs tracking-wider border-b border-white/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)] ${
                          sub.gold
                            ? "text-[#c9a84c] hover:bg-[#c9a84c]/5"
                            : location === sub.href
                            ? "text-[var(--tfrs-red-bright)] bg-white/5"
                            : "text-white hover:text-white/90 hover:bg-white/5"
                        }`}
                      >
                        <span>↳ {sub.label}</span>
                        {sub.comingSoon && (
                          <span className="ml-auto text-xs font-bold uppercase tracking-widest text-[#c9a84c] bg-[#c9a84c]/10 border border-[#c9a84c]/30 px-1.5 py-0.5 rounded">
                            Soon
                          </span>
                        )}
                      </Link>
                    ))}
                </div>
              );
            }

            return (
              <Link
                key={item.label}
                href={item.href!}
                className={`block px-5 py-4 font-eyebrow text-sm tracking-wider border-b border-white/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)] ${
                  item.gold
                    ? "text-[#c9a84c] hover:bg-[#c9a84c]/5"
                    : location === item.href
                    ? "text-[var(--tfrs-red-bright)] bg-white/5"
                    : "text-white hover:bg-white/5"
                }`}
              >
                {item.label}
              </Link>
            );
          })}

          {/* Mobile footer CTAs */}
          <div className="p-4 flex flex-col gap-3 border-t border-white/10">
            <Link
              href="/quote"
              className="btn-cta w-full justify-center text-xs px-5 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
            >
              <span>Request a Quote</span>
            </Link>
            <div className="grid grid-cols-2 gap-3">
              <a href="tel:+18322252838" className="btn-chip justify-center focus-visible:outline-none">
                <Phone size={14} />
                Call: {PHONE_DISPLAY}
              </a>
              <a href={SMS_URI} className="btn-chip justify-center focus-visible:outline-none">
                <MessageSquareText size={14} />
                Text: {SMS_PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
